export type SupportedCurrency = "INR" | "USD" | "AED";

export const EXCHANGE_RATES = {
  // Base: INR
  INR_TO_AED: 1 / 22.8,
  INR_TO_USD: 1 / 84.5,
  // Base: AED
  AED_TO_INR: 22.8,
  AED_TO_USD: 1 / 3.67, // 0.2725
  // Base: USD
  USD_TO_INR: 84.5,
  USD_TO_AED: 3.67,
};

/**
 * Converts a numerical deal value between INR, USD, and AED.
 */
export function convertCurrency(
  value: number,
  from: SupportedCurrency | string,
  to: SupportedCurrency | string
): number {
  if (!value || isNaN(value)) return 0;
  if (from === to) return value;

  const validFrom: SupportedCurrency = from === "AED" || from === "USD" ? from : "INR";
  const validTo: SupportedCurrency = to === "AED" || to === "USD" ? to : "INR";

  if (validFrom === validTo) return value;

  // Step 1: Normalize to INR
  let inINR = value;
  if (validFrom === "AED") inINR = value * EXCHANGE_RATES.AED_TO_INR;
  if (validFrom === "USD") inINR = value * EXCHANGE_RATES.USD_TO_INR;

  // Step 2: Convert from INR to target currency
  if (validTo === "INR") return Math.round(inINR);
  if (validTo === "AED") return Math.round(inINR * EXCHANGE_RATES.INR_TO_AED);
  if (validTo === "USD") return Math.round(inINR * EXCHANGE_RATES.INR_TO_USD);

  return value;
}

/**
 * Formats a deal value with appropriate currency symbol & locale separators.
 */
export function formatCurrencyValue(
  value: number,
  currency: SupportedCurrency | string = "INR"
): string {
  const curr: SupportedCurrency = currency === "AED" || currency === "USD" ? currency : "INR";
  const num = Math.round(value || 0);

  switch (curr) {
    case "AED":
      return `AED ${num.toLocaleString("en-AE")}`;
    case "USD":
      return `$${num.toLocaleString("en-US")}`;
    case "INR":
    default:
      return `₹${num.toLocaleString("en-IN")}`;
  }
}

/**
 * Returns flag and code badge label.
 */
export function getCurrencyBadge(currency: SupportedCurrency | string = "INR"): string {
  switch (currency) {
    case "AED":
      return "🇦🇪 AED";
    case "USD":
      return "🇺🇸 USD";
    case "INR":
    default:
      return "🇮🇳 INR";
  }
}

/**
 * Detects target currency from city, location, or country string.
 */
export function detectCurrencyFromLocation(location: string): SupportedCurrency | null {
  if (!location) return null;
  const lower = location.toLowerCase();

  if (
    lower.includes("dubai") ||
    lower.includes("uae") ||
    lower.includes("abu dhabi") ||
    lower.includes("sharjah") ||
    lower.includes("emirates") ||
    lower.includes("ajman")
  ) {
    return "AED";
  }

  if (
    lower.includes("usa") ||
    lower.includes("united states") ||
    lower.includes("new york") ||
    lower.includes("california") ||
    lower.includes("london") ||
    lower.includes("uk") ||
    lower.includes("singapore") ||
    lower.includes("canada")
  ) {
    return "USD";
  }

  if (
    lower.includes("bangalore") ||
    lower.includes("bengaluru") ||
    lower.includes("mumbai") ||
    lower.includes("delhi") ||
    lower.includes("hyderabad") ||
    lower.includes("chennai") ||
    lower.includes("pune") ||
    lower.includes("india")
  ) {
    return "INR";
  }

  return null;
}
